import { supabase } from '../../lib/supabase';
import { salesActivitiesService } from './activities.service';
import { salesContactsService } from './contacts.service';
export class SalesEmailService {
    /**
     * Fetch emails from the email proxy via Supabase edge function
     */
    async fetchEmails(userId, folder = 'INBOX', limit = 50) {
        try {
            const { data, error } = await supabase.functions.invoke('fetch-emails', {
                body: {
                    userId,
                    folder,
                    limit
                }
            });
            if (error) {
                console.error('Error fetching emails:', error);
                throw error;
            }
            if (!data.success) {
                console.warn('Email fetch unsuccessful:', data.message);
                return [];
            }
            return data.emails || [];
        }
        catch (error) {
            console.error('Error in fetchEmails:', error);
            throw error;
        }
    }
    /**
     * Sync fetched emails to sales_activities table
     * Automatically links emails to contacts by matching email addresses
     */
    async syncEmailsToActivities(organizationId, userId, emails) {
        const result = {
            success: true,
            emailsFetched: emails.length,
            emailsSynced: 0,
            errors: []
        };
        for (const email of emails) {
            try {
                // Find contact by email address (from sender or recipients)
                const contactEmail = email.from.email;
                const contact = await this.findContactByEmail(organizationId, contactEmail);
                // Check if email already exists in activities (avoid duplicates)
                const existingActivity = await this.findActivityByEmailId(organizationId, email.messageId || email.id);
                if (existingActivity) {
                    console.log(`Email ${email.id} already synced, skipping...`);
                    continue;
                }
                // Determine direction (inbound if from external, outbound if from user)
                const userProfile = await this.getUserProfile(userId);
                const userEmail = userProfile?.email || '';
                const direction = email.from.email.toLowerCase() === userEmail.toLowerCase()
                    ? 'outbound'
                    : 'inbound';
                // Log email as activity
                const activity = await salesActivitiesService.logEmail(organizationId, userId, contact?.id || '', // Empty string if no contact found
                {
                    subject: email.subject,
                    body: email.body,
                    from: email.from.email,
                    to: email.to,
                    cc: email.cc,
                    direction: direction,
                    messageId: email.messageId || email.id,
                    threadId: email.threadId
                });
                if (activity) {
                    result.emailsSynced++;
                }
            }
            catch (error) {
                const errorMessage = error instanceof Error ? error.message : 'Unknown error';
                result.errors.push(`Failed to sync email ${email.id}: ${errorMessage}`);
                console.error(`Error syncing email ${email.id}:`, error);
            }
        }
        result.success = result.errors.length === 0;
        return result;
    }
    /**
     * Fetch and sync emails in one operation
     */
    async fetchAndSyncEmails(organizationId, userId, folder = 'INBOX', limit = 50) {
        try {
            // Fetch emails from email proxy
            const emails = await this.fetchEmails(userId, folder, limit);
            // Sync to sales activities
            const result = await this.syncEmailsToActivities(organizationId, userId, emails);
            return result;
        }
        catch (error) {
            console.error('Error in fetchAndSyncEmails:', error);
            return {
                success: false,
                emailsFetched: 0,
                emailsSynced: 0,
                errors: [error instanceof Error ? error.message : 'Unknown error']
            };
        }
    }
    /**
     * Get recent email activities for the Inbox view
     */
    async getEmailActivities(organizationId, limit = 50) {
        return salesActivitiesService.getUnreadEmails(organizationId);
    }
    /**
     * Find contact by email address
     */
    async findContactByEmail(organizationId, email) {
        try {
            const { data, error } = await supabase
                .from('sales_contacts')
                .select('*')
                .eq('organization_id', organizationId)
                .ilike('email', email)
                .limit(1)
                .single();
            if (error || !data) {
                return null;
            }
            return salesContactsService['transformContact'](data);
        }
        catch (error) {
            console.error('Error finding contact by email:', error);
            return null;
        }
    }
    /**
     * Check if email activity already exists
     */
    async findActivityByEmailId(organizationId, emailMessageId) {
        try {
            const { data, error } = await supabase
                .from('sales_activities')
                .select('id')
                .eq('organization_id', organizationId)
                .eq('email_message_id', emailMessageId)
                .limit(1)
                .single();
            return data ? true : false;
        }
        catch (error) {
            return false;
        }
    }
    /**
     * Get user profile
     */
    async getUserProfile(userId) {
        try {
            const { data, error } = await supabase
                .from('profiles')
                .select('email, full_name')
                .eq('id', userId)
                .single();
            return data || null;
        }
        catch (error) {
            console.error('Error fetching user profile:', error);
            return null;
        }
    }
    /**
     * Auto-create contact from email if not exists
     */
    async createContactFromEmail(organizationId, userId, emailAddress, fullName) {
        try {
            // Check if contact already exists
            const existing = await this.findContactByEmail(organizationId, emailAddress);
            if (existing) {
                return existing;
            }
            // Create new contact
            const contact = await salesContactsService.createContact(organizationId, userId, {
                fullName: fullName || emailAddress,
                email: emailAddress,
                contactType: 'person',
                stage: 'lead'
            });
            return contact;
        }
        catch (error) {
            console.error('Error creating contact from email:', error);
            return null;
        }
    }
}
// Export singleton instance
export const salesEmailService = new SalesEmailService();
